import { useEffect, useMemo, useState } from 'react';
import { getCertificationOverview } from '../../services/certificationService';
import './AdminCertificationOverview.css';

const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const statusClass = (status) =>
  `aco-status aco-status--${String(status || 'ACTIVE').toLowerCase()}`;

const roleLabel = (person) => {
  if (person?.role === 'ADMIN') return 'Admin';
  if (person?.role === 'MEMBER') return 'Member';
  return 'Team member';
};

export default function AdminCertificationOverview() {
  const [partners, setPartners] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [members, setMembers] = useState([]);
  const [partnerOpen, setPartnerOpen] = useState({});
  const [memberOpen, setMemberOpen] = useState({});
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [partnerFilter, setPartnerFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await getCertificationOverview();
      setPartners(data?.partners || []);
      setCertifications(data?.certifications || []);
      setMembers(data?.members || []);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load certification overview.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const allTypes = useMemo(
    () => partners.flatMap((p) => p.certificationTypes || []),
    [partners]
  );

  const memberCerts = (memberId) =>
    certifications.filter((c) => c.user?.id === memberId);

  const partnerCerts = (partnerId) =>
    certifications.filter((c) => c.certificationType?.partner?.id === partnerId);

  const totalTypes = allTypes.length;
  const active = certifications.filter((c) => c.status === 'ACTIVE').length;
  const expiring = certifications.filter((c) => c.status === 'EXPIRING_SOON').length;
  const expired = certifications.filter((c) => c.status === 'EXPIRED').length;

  const overallProgress = members.length && totalTypes
    ? Math.round(
        (members.reduce((sum, member) => {
          const unique = new Set(memberCerts(member.id).map((c) => c.certificationTypeId));
          return sum + Math.min(unique.size, totalTypes);
        }, 0) / (members.length * totalTypes)) * 100
      )
    : 0;

  const visiblePartners = partners.filter((partner) => {
    if (partnerFilter && partner.id !== partnerFilter) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    const text = [
      partner.name,
      partner.status,
      ...(partner.certificationTypes || []).map((ct) => ct.name),
    ].filter(Boolean).join(' ').toLowerCase();
    return text.includes(q);
  });

  const visibleMembers = members.filter((member) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    const text = [
      member.name,
      member.email,
      roleLabel(member),
      ...memberCerts(member.id).map((c) => c.certificationType?.name),
      ...memberCerts(member.id).map((c) => c.certificationType?.partner?.name),
    ].filter(Boolean).join(' ').toLowerCase();
    return text.includes(q);
  });

  const filteredMemberCerts = (memberId) =>
    memberCerts(memberId).filter((c) => {
      if (status && c.status !== status) return false;
      if (partnerFilter && c.certificationType?.partner?.id !== partnerFilter) return false;
      return true;
    });

  return (
    <section className="aco-page">
      <header className="aco-hero-wrap">
        <div className="aco-hero-bg" aria-hidden="true" />
        <div className="aco-hero-grid" aria-hidden="true" />
        <div className="aco-hero-inner">
          <div>
            <div className="aco-eyebrow">
              <span className="aco-live-dot" aria-hidden="true" /> CERTIFICATION DIRECTORY
            </div>
            <h1>Partners &amp; Certification Overview</h1>
            <p>
              A read-only view of the partner certification catalog and every
              team member's certification progress — Admins and Members alike.
            </p>
          </div>
          <button type="button" className="aco-refresh" onClick={loadData} disabled={loading}>
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </header>

      <div className="aco-shell">
        {error ? <div className="aco-alert">{error}</div> : null}

        {loading ? (
          <div className="aco-loading">Loading partner and certification data...</div>
        ) : (
          <>
            <div className="aco-stats">
              <div><span>Partners</span><strong>{partners.length}</strong><small>certification providers</small></div>
              <div><span>Certification types</span><strong>{totalTypes}</strong><small>in the partner catalog</small></div>
              <div><span>Team members</span><strong>{members.length}</strong><small>Admins &amp; Members with progress tracking</small></div>
              <div><span>Overall progress</span><strong>{overallProgress}%</strong><small>catalog completion</small></div>
            </div>

            <div className="aco-health">
              <span><i className="aco-dot aco-dot--active" /> <b>{active}</b> Active</span>
              <span><i className="aco-dot aco-dot--soon" /> <b>{expiring}</b> Expiring soon</span>
              <span><i className="aco-dot aco-dot--expired" /> <b>{expired}</b> Expired</span>
            </div>

            <div className="aco-toolbar">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search partners, certifications or team members..."
              />
              <select value={partnerFilter} onChange={(e) => setPartnerFilter(e.target.value)}>
                <option value="">All partners</option>
                {partners.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <select value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">All certification status</option>
                <option value="ACTIVE">Active</option>
                <option value="EXPIRING_SOON">Expiring soon</option>
                <option value="EXPIRED">Expired</option>
              </select>
              <button type="button" className="aco-clear" onClick={() => { setSearch(''); setPartnerFilter(''); setStatus(''); }}>
                Clear
              </button>
            </div>

            <section className="aco-section">
              <div className="aco-section-head">
                <div><span className="aco-kicker">01 / PARTNER CATALOG</span><h2>Partners &amp; Certifications</h2></div>
                <span className="aco-readonly">READ ONLY</span>
              </div>

              <div className="aco-partners">
                {visiblePartners.map((partner) => {
                  const open = !!partnerOpen[partner.id];
                  const partnerRecords = partnerCerts(partner.id);
                  return (
                    <article className="aco-partner" key={partner.id}>
                      <button
                        type="button"
                        className="aco-partner-head"
                        onClick={() => setPartnerOpen((v) => ({ ...v, [partner.id]: !open }))}
                      >
                        <span className="aco-logo">
                          {partner.logo ? <img src={partner.logo} alt="" /> : partner.name?.charAt(0)}
                        </span>
                        <span className="aco-partner-title">
                          <strong>{partner.name}</strong>
                          <small>{partner.status} · {partner.certificationTypes?.length || 0} certification types · {partnerRecords.length} member records</small>
                        </span>
                        <span className={`aco-chevron ${open ? 'open' : ''}`}>⌄</span>
                      </button>

                      {open ? (
                        <div className="aco-cert-list">
                          {(partner.certificationTypes || []).map((type) => {
                            const holders = partnerRecords.filter((c) => c.certificationTypeId === type.id);
                            return (
                              <div className="aco-cert-row" key={type.id}>
                                <div className="aco-cert-main">
                                  <span className="aco-cert-mark">/</span>
                                  <div>
                                    <strong>{type.name}</strong>
                                    <small>
                                      {type.validityMonths ? `${type.validityMonths} month validity` : 'No expiry configured'}
                                      {' · '}
                                      {holders.length} holder{holders.length === 1 ? '' : 's'}
                                    </small>
                                  </div>
                                </div>
                                <div className="aco-holder-chips">
                                  {holders.length ? holders.slice(0, 5).map((cert) => (
                                    <span key={cert.id} title={cert.user?.email}>
                                      {(cert.user?.name || cert.user?.email || 'M').charAt(0).toUpperCase()}
                                    </span>
                                  )) : <em>No members yet</em>}
                                  {holders.length > 5 ? <small>+{holders.length - 5}</small> : null}
                                </div>
                              </div>
                            );
                          })}
                          {!partner.certificationTypes?.length ? (
                            <div className="aco-empty">No certifications configured for this partner.</div>
                          ) : null}
                        </div>
                      ) : null}
                    </article>
                  );
                })}
                {!visiblePartners.length ? <div className="aco-empty aco-empty--large">No partners match your filters.</div> : null}
              </div>
            </section>

            <section className="aco-section">
              <div className="aco-section-head">
                <div><span className="aco-kicker">02 / TEAM PROGRESS</span><h2>Team Certification Progress</h2></div>
                <span className="aco-readonly">READ ONLY</span>
              </div>

              <div className="aco-members">
                {visibleMembers.map((member) => {
                  const records = filteredMemberCerts(member.id);
                  const uniqueCount = new Set(memberCerts(member.id).map((c) => c.certificationTypeId)).size;
                  const progress = totalTypes ? Math.min(Math.round((uniqueCount / totalTypes) * 100), 100) : 0;
                  const memberActive = memberCerts(member.id).filter((c) => c.status === 'ACTIVE').length;
                  const memberSoon = memberCerts(member.id).filter((c) => c.status === 'EXPIRING_SOON').length;
                  const memberExpired = memberCerts(member.id).filter((c) => c.status === 'EXPIRED').length;
                  const open = !!memberOpen[member.id];

                  return (
                    <article className="aco-member" key={member.id}>
                      <div className="aco-member-head">
                        <div className="aco-member-identity">
                          <span className="aco-avatar">
                            {(member.name || member.email || '?').charAt(0).toUpperCase()}
                          </span>
                          <div>
                            <strong>
                              {member.name || member.email || 'Unnamed team member'}
                              <span className={`aco-role-badge aco-role-badge--${member.role === 'ADMIN' ? 'admin' : 'member'}`}>
                                {roleLabel(member)}
                              </span>
                            </strong>
                            {member.name && member.email ? <small>{member.email}</small> : null}
                          </div>
                        </div>
                        <div className="aco-member-total"><strong>{progress}%</strong><span>complete</span></div>
                      </div>

                      <div className="aco-progress-track"><span style={{ width: `${progress}%` }} /></div>
                      <div className="aco-progress-meta">
                        <span>{uniqueCount} of {totalTypes} certification types</span>
                        <span><b>{memberActive}</b> active · <b>{memberSoon}</b> expiring · <b>{memberExpired}</b> expired</span>
                      </div>

                      <button
                        type="button"
                        className="aco-member-toggle"
                        onClick={() => setMemberOpen((v) => ({ ...v, [member.id]: !open }))}
                      >
                        {open ? 'Hide certification records' : `View ${records.length} matching record${records.length === 1 ? '' : 's'}`}
                        <span>{open ? '↑' : '↓'}</span>
                      </button>

                      {open ? (
                        <div className="aco-member-records">
                          {!records.length ? <div className="aco-empty">No records match the current filters.</div> : null}
                          {records.map((cert) => (
                            <div className="aco-record" key={cert.id}>
                              <div>
                                <span>{cert.certificationType?.partner?.name}</span>
                                <strong>{cert.certificationType?.name}</strong>
                              </div>
                              <div><small>Completed</small><strong>{formatDate(cert.completedDate)}</strong></div>
                              <div><small>Expires</small><strong>{formatDate(cert.expiryDate)}</strong></div>
                              <span className={statusClass(cert.status)}>{String(cert.status || '').replace('_', ' ')}</span>
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </article>
                  );
                })}
                {!visibleMembers.length ? <div className="aco-empty aco-empty--large">No team members match your search.</div> : null}
              </div>
            </section>
          </>
        )}
      </div>
    </section>
  );
}